import React, { useCallback, useEffect, useMemo, useState } from "react";
import { QRCode, QRCodeForm as QRFormData, QRFolder } from "./types";
import { QRService } from "./services/qrService";
import Dashboard from "./components/Dashboard";
import QRCodeForm from "./components/QRCodeForm";
import QRCodeList from "./components/QRCodeList";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import {
  migrateExistingQRCodes,
  checkMigrationNeeded,
} from "./utils/migrateQRCodes";
import AuthModal from "./components/Auth/AuthModal";
import AppShell, { AppView } from "./components/Layout/AppShell";
import LoadingAnimation from "./components/LoadingAnimation";
import MarketingSite from "./components/Marketing/MarketingSite";
import QRCodeDetails from "./components/QRCodeDetails";
import WorkspaceManager from "./components/WorkspaceManager";
import { WorkspaceService } from "./services/workspaceService";
import NotificationsPanel from "./components/NotificationsPanel";
import EmailPreferences from "./components/EmailPreferences";

const defaultStyleOptions: NonNullable<QRFormData["styleOptions"]> = {
  size: 256,
  margin: 2,
  color: { dark: "#111827", light: "#FFFFFF" },
  errorCorrectionLevel: "M",
  logo: { enabled: false },
  style: { transparentBackground: false },
};

const AppContent: React.FC = () => {
  const developmentPreview =
    process.env.NODE_ENV === "development" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
    new URLSearchParams(window.location.search).has("preview");
  const previewView = new URLSearchParams(window.location.search).get(
    "preview",
  );
  const previewViews: AppView[] = [
    "dashboard",
    "codes",
    "create",
    "analytics",
    "health",
    "notifications",
    "campaigns",
    "landing",
    "protection",
    "workspace",
    "domains",
    "settings",
  ];
  const [currentView, setCurrentView] = useState<AppView>(
    developmentPreview && previewViews.includes(previewView as AppView)
      ? (previewView as AppView)
      : "dashboard",
  );
  const [qrCodes, setQrCodes] = useState<QRCode[]>([]);
  const [selectedQRCode, setSelectedQRCode] = useState<QRCode | null>(null);
  const [duplicateDraft, setDuplicateDraft] = useState<
    QRFormData | undefined
  >();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [folders, setFolders] = useState<QRFolder[]>([]);
  const [folderFilter, setFolderFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState<"newest" | "scans" | "name" | "health">(
    "newest",
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { currentUser, loading: authLoading } = useAuth();

  const loadQRCodes = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      setQrCodes(await QRService.getAllQRCodes());
    } catch (loadError) {
      setError(
        "QR codes could not be loaded. Please check the Firebase connection.",
      );
      console.error("Load error:", loadError);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!authLoading && currentUser) void loadQRCodes();
  }, [authLoading, currentUser, loadQRCodes]);

  useEffect(() => {
    if (currentUser)
      void WorkspaceService.getFolders()
        .then(setFolders)
        .catch((error) => console.error("Folder load failed", error));
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    const migrationKey = "qr_styleOptions_migration_completed";
    if (localStorage.getItem(migrationKey)) return;

    const migrate = async () => {
      try {
        if (await checkMigrationNeeded()) {
          await migrateExistingQRCodes();
          await loadQRCodes();
        }
        localStorage.setItem(migrationKey, "true");
      } catch (migrationError) {
        console.error("Migration error:", migrationError);
      }
    };

    void migrate();
  }, [currentUser, loadQRCodes]);

  const filteredQRCodes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = qrCodes.filter((qrCode) => {
      if (
        folderFilter !== "all" &&
        (folderFilter === "none"
          ? Boolean(qrCode.folderId)
          : qrCode.folderId !== folderFilter)
      )
        return false;
      if (statusFilter === "favorites" && !qrCode.favorite) return false;
      if (statusFilter === "archived" && qrCode.status !== "archived")
        return false;
      if (
        statusFilter === "unhealthy" &&
        !["unreachable", "slow", "declining", "inactive"].includes(
          qrCode.health?.status || "",
        )
      )
        return false;
      if (statusFilter === "unscanned" && qrCode.accessCount > 0) return false;
      if (statusFilter === "expiring") {
        const dates = [qrCode.protection?.expiresAt, qrCode.schedule?.endsAt]
          .filter(Boolean)
          .map((value) => Date.parse(value as string))
          .filter(Number.isFinite);
        const withinThirtyDays = dates.some(
          (timestamp) =>
            timestamp >= Date.now() && timestamp <= Date.now() + 30 * 86400000,
        );
        if (!withinThirtyDays) return false;
      }
      if (statusFilter === "active" && qrCode.status === "archived")
        return false;
      if (!query) return true;
      return [
        qrCode.id,
        qrCode.title,
        qrCode.description,
        qrCode.target,
        ...(qrCode.tags || []),
      ]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(query));
    });
    return filtered.sort((a, b) =>
      sortBy === "scans"
        ? b.accessCount - a.accessCount
        : sortBy === "name"
          ? (a.title || a.id).localeCompare(b.title || b.id)
          : sortBy === "health"
            ? String(a.health?.status || "").localeCompare(
                String(b.health?.status || ""),
              )
            : b.createdAt.getTime() - a.createdAt.getTime(),
    );
  }, [qrCodes, searchQuery, folderFilter, statusFilter, sortBy]);

  const bulkMove = async (folderId: string) => {
    await Promise.all(
      selectedIds.map((id) => QRService.updateQRCode(id, { folderId })),
    );
    setSelectedIds([]);
    await loadQRCodes();
    showMessage("success", "Ausgewählte QR-Codes wurden verschoben.");
  };

  const bulkArchive = async () => {
    await Promise.all(
      selectedIds.map((id) =>
        QRService.updateQRCode(id, {
          status: "archived",
          changeComment: "Per Mehrfachauswahl archiviert",
        }),
      ),
    );
    setSelectedIds([]);
    await loadQRCodes();
    showMessage("success", "Ausgewählte QR-Codes wurden archiviert.");
  };

  const showMessage = (type: "success" | "error", message: string) => {
    if (type === "success") {
      setSuccessMessage(message);
      window.setTimeout(() => setSuccessMessage(""), 4500);
      return;
    }
    setError(message);
    window.setTimeout(() => setError(""), 5000);
  };

  const handleCreateQRCode = async (formData: QRFormData) => {
    try {
      setSubmitting(true);
      setError("");
      await QRService.createQRCode(formData);
      await loadQRCodes();
      setDuplicateDraft(undefined);
      setCurrentView("codes");
      showMessage("success", `“${formData.title || formData.id}” is live.`);
    } catch (createError) {
      showMessage(
        "error",
        createError instanceof Error
          ? createError.message
          : "QR code could not be created.",
      );
      console.error("Create error:", createError);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateQRCode = async (formData: QRFormData) => {
    if (!selectedQRCode) return;

    try {
      setSubmitting(true);
      setError("");
      await QRService.updateQRCode(selectedQRCode.id, formData);
      await loadQRCodes();
      setSelectedQRCode(null);
      setCurrentView("codes");
      showMessage("success", `“${formData.title || formData.id}” was updated.`);
    } catch (updateError) {
      showMessage(
        "error",
        updateError instanceof Error
          ? updateError.message
          : "QR code could not be updated.",
      );
      console.error("Update error:", updateError);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteQRCode = async (qrCode: QRCode) => {
    try {
      setError("");
      await QRService.deleteQRCode(qrCode.id);
      await loadQRCodes();
      showMessage("success", `“${qrCode.title || qrCode.id}” was deleted.`);
    } catch (deleteError) {
      showMessage(
        "error",
        deleteError instanceof Error
          ? deleteError.message
          : "QR code could not be deleted.",
      );
      console.error("Delete error:", deleteError);
    }
  };

  const handleViewChange = (view: AppView) => {
    if (view !== "edit" && view !== "details") setSelectedQRCode(null);
    if (view !== "create") setDuplicateDraft(undefined);
    setCurrentView(view === "edit" && !selectedQRCode ? "dashboard" : view);
  };

  if (authLoading)
    return <LoadingAnimation message="Opening your workspace..." />;

  if (!currentUser && !developmentPreview) {
    return (
      <>
        <MarketingSite onSignIn={() => setShowAuthModal(true)} />
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode="login"
        />
      </>
    );
  }

  const renderContent = () => {
    if (
      ["create", "campaigns", "landing", "protection"].includes(currentView)
    ) {
      return (
        <QRCodeForm
          initialData={duplicateDraft}
          onSubmit={handleCreateQRCode}
          onCancel={() => setCurrentView("dashboard")}
          isSubmitting={submitting}
        />
      );
    }

    if (currentView === "edit" && selectedQRCode) {
      return (
        <QRCodeForm
          initialData={{
            id: selectedQRCode.id,
            target: selectedQRCode.target,
            title: selectedQRCode.title || "",
            description: selectedQRCode.description || "",
            styleOptions: selectedQRCode.styleOptions || defaultStyleOptions,
            status: selectedQRCode.status || "active",
            favorite: selectedQRCode.favorite || false,
            folderId: selectedQRCode.folderId || "",
            tags: selectedQRCode.tags || [],
            notes: selectedQRCode.notes || "",
            fallbackTarget:
              selectedQRCode.fallbackTarget || selectedQRCode.target,
            utm: selectedQRCode.utm || { enabled: false },
            schedule: selectedQRCode.schedule || {
              enabled: false,
              timezone: "Europe/Berlin",
            },
            smartRules: selectedQRCode.smartRules || [],
            protection: selectedQRCode.protection || { enabled: false },
            landingPage: selectedQRCode.landingPage || { enabled: false },
            changeComment: "",
          }}
          onSubmit={handleUpdateQRCode}
          onCancel={() => {
            setSelectedQRCode(null);
            setCurrentView("codes");
          }}
          isEditing
          isSubmitting={submitting}
        />
      );
    }

    if (currentView === "details" && selectedQRCode) {
      return (
        <QRCodeDetails
          qrCode={selectedQRCode}
          onBack={() => setCurrentView("codes")}
          onEdit={() => setCurrentView("edit")}
          onChanged={async () => {
            await loadQRCodes();
            const refreshed = await QRService.getQRCode(selectedQRCode.id);
            if (refreshed) setSelectedQRCode(refreshed);
          }}
        />
      );
    }

    if (currentView === "settings") return <SettingsPanel />;
    if (currentView === "workspace") return <SettingsPanel focus="workspace" />;
    if (currentView === "domains") return <SettingsPanel focus="domains" />;

    if (currentView === "notifications") {
      return (
        <div className="space-y-6">
          <FeatureIntro
            title="Warnungen und Leads an einem Ort"
            description="Health-Probleme, neue Kontakte und wichtige Systemhinweise bleiben hier dauerhaft auffindbar."
          />
          <NotificationsPanel />
        </div>
      );
    }

    if (currentView === "analytics" || currentView === "health") {
      const healthCodes = qrCodes.filter((item) =>
        ["unreachable", "slow", "declining", "inactive", "unscanned"].includes(
          item.health?.status || (item.accessCount ? "active" : "unscanned"),
        ),
      );
      const visibleCodes =
        currentView === "health"
          ? healthCodes
          : [...qrCodes].sort((a, b) => b.accessCount - a.accessCount);
      return (
        <div className="space-y-6">
          {currentView === "analytics" ? (
            <>
              <FeatureIntro
                title="Performance über alle QR-Codes"
                description="Öffne einen QR-Code für Tages-, Wochen- und Monatsverläufe sowie CSV- und PDF-Berichte."
              />
              <Dashboard />
            </>
          ) : (
            <FeatureIntro
              title="Gedruckte Codes zuverlässig überwachen"
              description={`${healthCodes.length} Codes benötigen Aufmerksamkeit. Verfügbarkeit, TLS, Geschwindigkeit und Redirects werden automatisch geprüft.`}
            />
          )}
          <section className="app-panel overflow-hidden">
            <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
              <h2 className="text-base font-semibold text-slate-950">
                {currentView === "analytics"
                  ? "QR-Codes nach Scans"
                  : "Health-Übersicht"}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Klicke einen Eintrag an, um Details und Aktionen zu öffnen.
              </p>
            </div>
            <QRCodeList
              qrCodes={visibleCodes}
              onEdit={(qrCode) => {
                setSelectedQRCode(qrCode);
                setCurrentView("edit");
              }}
              onOpen={(qrCode) => {
                setSelectedQRCode(qrCode);
                setCurrentView("details");
              }}
              onDelete={handleDeleteQRCode}
              onCreate={() => setCurrentView("create")}
              loading={loading}
              compact
            />
          </section>
        </div>
      );
    }

    if (currentView === "codes") {
      return (
        <section className="app-panel overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div>
              <h2 className="text-base font-semibold text-slate-950">
                All QR codes
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {filteredQRCodes.length} of {qrCodes.length} items
              </p>
            </div>
            <button
              type="button"
              onClick={() => void loadQRCodes()}
              disabled={loading}
              className="app-secondary-button"
            >
              <svg
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v6h-6" />
              </svg>
              Refresh
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-5 py-3 sm:px-6">
            <select
              value={folderFilter}
              onChange={(event) => setFolderFilter(event.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold"
            >
              <option value="all">Alle Ordner</option>
              <option value="none">Ohne Ordner</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="favorites">Favoriten</option>
              <option value="unhealthy">Health-Warnung</option>
              <option value="unscanned">Ungescannt</option>
              <option value="expiring">Läuft in 30 Tagen ab</option>
              <option value="archived">Archiviert</option>
            </select>
            <select
              value={sortBy}
              onChange={(event) =>
                setSortBy(event.target.value as typeof sortBy)
              }
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold"
            >
              <option value="newest">Neueste zuerst</option>
              <option value="scans">Meiste Scans</option>
              <option value="name">Name</option>
              <option value="health">Health-Status</option>
            </select>
            <button
              type="button"
              onClick={() =>
                setSelectedIds(
                  selectedIds.length === filteredQRCodes.length
                    ? []
                    : filteredQRCodes.map((item) => item.id),
                )
              }
              className="ml-auto text-xs font-semibold text-blue-600"
            >
              {selectedIds.length ? "Auswahl aufheben" : "Alle auswählen"}
            </button>
          </div>
          {selectedIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-b border-blue-200 bg-blue-50 px-5 py-3 text-xs font-semibold text-blue-900 sm:px-6">
              <span>{selectedIds.length} ausgewählt</span>
              <select
                defaultValue=""
                onChange={(event) => {
                  if (event.target.value)
                    void bulkMove(
                      event.target.value === "__none__"
                        ? ""
                        : event.target.value,
                    );
                }}
                className="ml-auto rounded-lg border border-blue-200 bg-white px-3 py-2"
              >
                <option value="">In Ordner verschieben …</option>
                <option value="__none__">Ohne Ordner</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => void bulkArchive()}
                className="rounded-lg bg-white px-3 py-2 text-blue-700"
              >
                Archivieren
              </button>
            </div>
          )}
          <QRCodeList
            qrCodes={filteredQRCodes}
            onEdit={(qrCode) => {
              setSelectedQRCode(qrCode);
              setCurrentView("edit");
            }}
            onOpen={(qrCode) => {
              setSelectedQRCode(qrCode);
              setCurrentView("details");
            }}
            onDuplicate={(qrCode) => {
              setDuplicateDraft({
                id: QRService.generateQRId(),
                target: qrCode.target,
                title: `${qrCode.title || qrCode.id} – Kopie`,
                description: qrCode.description || "",
                styleOptions: qrCode.styleOptions || defaultStyleOptions,
                status: "active",
                favorite: false,
                folderId: qrCode.folderId,
                tags: qrCode.tags || [],
                notes: qrCode.notes || "",
                fallbackTarget: qrCode.fallbackTarget || qrCode.target,
                utm: qrCode.utm,
                schedule: qrCode.schedule,
                smartRules: qrCode.smartRules,
                protection: { enabled: false },
                landingPage: qrCode.landingPage,
                changeComment: "QR-Code dupliziert",
              });
              setCurrentView("create");
            }}
            onToggleFavorite={async (qrCode) => {
              await QRService.updateQRCode(qrCode.id, {
                favorite: !qrCode.favorite,
              });
              await loadQRCodes();
            }}
            onToggleArchive={async (qrCode) => {
              await QRService.updateQRCode(qrCode.id, {
                status: qrCode.status === "archived" ? "active" : "archived",
                changeComment:
                  qrCode.status === "archived"
                    ? "QR-Code reaktiviert"
                    : "QR-Code archiviert",
              });
              await loadQRCodes();
            }}
            onDelete={handleDeleteQRCode}
            onCreate={() => setCurrentView("create")}
            loading={loading}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        </section>
      );
    }

    return (
      <div className="space-y-6">
        <section className="overview-hero">
          <div className="relative z-10 max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-blue-100">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Alle
              Redirects online
            </span>
            <h2 className="mt-4 text-2xl font-semibold tracking-[-0.03em] text-white sm:text-3xl">
              Deine QR-Codes. Klar im Blick.
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
              Verwalte Ziele, prüfe den Zustand gedruckter Codes und erkenne
              Scan-Aktivität ohne Umwege.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setCurrentView("create")}
                className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-black/20 transition hover:-translate-y-0.5 hover:bg-blue-50"
              >
                <span className="text-lg leading-none">+</span> QR-Code
                erstellen
              </button>
              <button
                type="button"
                onClick={() => setCurrentView("codes")}
                className="inline-flex items-center rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Bibliothek öffnen
              </button>
            </div>
          </div>
          <div className="overview-health-card" aria-hidden="true">
            <span className="overview-health-dot" />
            <strong>Systemstatus</strong>
            <small>Alle Dienste erreichbar</small>
          </div>
        </section>

        <Dashboard />

        <NotificationsPanel />

        <section className="app-panel overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
            <div>
              <h2 className="text-base font-semibold text-slate-950">
                Zuletzt bearbeitet
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Deine neuesten Redirects und Scan-Aktivitäten.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCurrentView("codes")}
              className="text-sm font-semibold text-blue-600 hover:text-blue-700"
            >
              Alle anzeigen
            </button>
          </div>
          <QRCodeList
            qrCodes={filteredQRCodes.slice(0, 4)}
            onEdit={(qrCode) => {
              setSelectedQRCode(qrCode);
              setCurrentView("edit");
            }}
            onOpen={(qrCode) => {
              setSelectedQRCode(qrCode);
              setCurrentView("details");
            }}
            onDelete={handleDeleteQRCode}
            onCreate={() => setCurrentView("create")}
            loading={loading}
            compact
          />
        </section>

        <section className="app-panel overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
            <div>
              <h2 className="text-base font-semibold text-slate-950">
                Top QR-Codes
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Nach Gesamtscans sortiert.
              </p>
            </div>
          </div>
          <QRCodeList
            qrCodes={[...qrCodes]
              .sort((a, b) => b.accessCount - a.accessCount)
              .slice(0, 4)}
            onEdit={(qrCode) => {
              setSelectedQRCode(qrCode);
              setCurrentView("edit");
            }}
            onOpen={(qrCode) => {
              setSelectedQRCode(qrCode);
              setCurrentView("details");
            }}
            onDelete={handleDeleteQRCode}
            compact
          />
        </section>
      </div>
    );
  };

  return (
    <>
      <AppShell
        currentView={currentView}
        onViewChange={handleViewChange}
        onCreate={() => setCurrentView("create")}
        qrCount={qrCodes.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      >
        {renderContent()}
      </AppShell>

      {(successMessage || error) && (
        <div
          role="status"
          className={`app-toast ${error ? "is-error" : "is-success"}`}
        >
          <span className="app-toast-icon">{error ? "!" : "✓"}</span>
          <div>
            <p className="text-sm font-semibold">
              {error ? "Something went wrong" : "Done"}
            </p>
            <p className="mt-0.5 text-sm opacity-80">
              {error || successMessage}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

const FeatureIntro: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <section className="app-panel overflow-hidden bg-gradient-to-br from-slate-950 to-slate-800 p-6 text-white sm:p-8">
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-300">
      RedirectQR Control Center
    </p>
    <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
      {title}
    </h2>
    <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300 sm:text-base">
      {description}
    </p>
  </section>
);

const SettingsPanel: React.FC<{ focus?: "workspace" | "domains" }> = ({
  focus,
}) => {
  const { currentUser, logout } = useAuth();
  const redirectDomain = process.env.REACT_APP_CUSTOM_DOMAIN || "redirectqr.de";
  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-6">
        <WorkspaceManager focus={focus} />
        {!focus && <EmailPreferences />}
        {!focus && (
          <section className="app-panel p-5 sm:p-6">
            <div className="border-b border-slate-200 pb-5">
              <h2 className="text-base font-semibold text-slate-950">
                Redirect configuration
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                The permanent public address encoded into new QR codes.
              </p>
            </div>
            <div className="mt-5">
              <label className="app-label">Public domain</label>
              <div className="mt-2 flex items-center rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm text-slate-700">
                https://{redirectDomain}/qr/
                <span className="text-slate-400">your-id</span>
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-500">
                Change this with <code>REACT_APP_CUSTOM_DOMAIN</code> before
                building the app.
              </p>
            </div>
          </section>
        )}
        {!focus && (
          <section className="app-panel p-5 sm:p-6">
            <div className="border-b border-slate-200 pb-5">
              <h2 className="text-base font-semibold text-slate-950">
                Firebase connection
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Runtime services used by this workspace.
              </p>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {["Authentication", "Firestore", "Storage"].map((service) => (
                <div
                  key={service}
                  className="rounded-xl border border-slate-200 p-4"
                >
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    {service}
                  </span>
                  <p className="mt-2 text-xs text-slate-500">Connected</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
      {!focus && (
        <aside className="app-panel h-fit p-5 sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
            Account
          </p>
          <div className="mt-4 flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-50 font-bold text-blue-700">
              {(currentUser?.displayName || currentUser?.email || "U")
                .charAt(0)
                .toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate font-semibold text-slate-950">
                {currentUser?.displayName || "Workspace admin"}
              </p>
              <p className="truncate text-sm text-slate-500">
                {currentUser?.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="app-secondary-button mt-6 w-full justify-center"
          >
            Sign out
          </button>
        </aside>
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
